import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };
const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const fmtCurrency = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const Tiers = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({
    nama: '', minimum_view: '', maximum_view: '', bounty_approved_value: '',
    payouts: [{ jumlah_view: '', jumlah_payout: '' }]
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/tiers');
      setTiers(data);
    } catch (error) {
      alert('Gagal memuat data: ' + error.message);
    } finally { setLoading(false); }
  };

  const handleOpenModal = (tier = null) => {
    if (tier) {
      setEditingTier(tier);
      setFormData({
        nama: tier.nama,
        minimum_view: tier.minimum_view,
        maximum_view: tier.maximum_view,
        bounty_approved_value: tier.bounty_approved_value || '',
        payouts: tier.tier_payouts?.length > 0
          ? tier.tier_payouts.map(p => ({ jumlah_view: p.jumlah_view, jumlah_payout: p.jumlah_payout }))
          : [{ jumlah_view: '', jumlah_payout: '' }]
      });
    } else {
      setEditingTier(null);
      setFormData({ nama: '', minimum_view: '', maximum_view: '', bounty_approved_value: '', payouts: [{ jumlah_view: '', jumlah_payout: '' }] });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTier(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        nama: formData.nama,
        minimum_view: parseInt(formData.minimum_view),
        maximum_view: parseInt(formData.maximum_view),
        bounty_approved_value: formData.bounty_approved_value !== '' ? parseFloat(formData.bounty_approved_value) : undefined,
        payouts: formData.payouts.map(p => ({ jumlah_view: parseInt(p.jumlah_view), jumlah_payout: parseFloat(p.jumlah_payout) }))
      };
      if (editingTier) {
        await api.put(`/api/tiers/${editingTier.id}`, submitData);
      } else {
        await api.post('/api/tiers', submitData);
      }
      handleCloseModal();
      loadData();
    } catch (error) { alert('Gagal menyimpan: ' + error.message); }
  };

  const handleDelete = async (tierId) => {
    if (!window.confirm('Hapus tier ini?')) return;
    try {
      await api.delete(`/api/tiers/${tierId}`);
      loadData();
    } catch (error) { alert('Gagal menghapus: ' + error.message); }
  };

  const addPayout   = () => setFormData(p => ({ ...p, payouts: [...p.payouts, { jumlah_view: '', jumlah_payout: '' }] }));
  const removePayout = (i) => { if (formData.payouts.length > 1) setFormData(p => ({ ...p, payouts: p.payouts.filter((_, idx) => idx !== i) })); };
  const updatePayout = (i, field, value) => setFormData(p => ({ ...p, payouts: p.payouts.map((px, idx) => idx === i ? { ...px, [field]: value } : px) }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Tier Management</h1>
          <p className="text-[13px] text-[#8a8a8a] mt-0.5">Kelola tier dan jenjang payout berdasarkan view count.</p>
        </div>
        <button onClick={() => handleOpenModal()}
          className="flex items-center justify-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors w-full sm:w-auto flex-shrink-0">
          + Tambah Tier
        </button>
      </div>

      {tiers.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center" style={card}>
          <p className="text-[14px] text-[#8a8a8a]">Belum ada tier. Klik "Tambah Tier" untuk membuat tier pertama.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden" style={card}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e5e5e5]">
                  {['Nama Tier', 'Range View', 'Bounty Approved', 'Jenjang Payout', 'Actions'].map(h => (
                    <th key={h} className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => (
                  <tr key={tier.id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-[13px] font-semibold text-[#3b3b3b] whitespace-nowrap">{tier.nama}</td>
                    <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">
                      {tier.minimum_view.toLocaleString('id-ID')} – {tier.maximum_view.toLocaleString('id-ID')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {tier.bounty_approved_value > 0 ? (
                        <span className="text-[13px] font-semibold text-[#3fb5d2]">{fmtCurrency(tier.bounty_approved_value)}</span>
                      ) : (
                        <span className="text-[12px] text-[#8a8a8a]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {tier.tier_payouts?.length > 0 ? (
                        <div className="space-y-0.5">
                          {tier.tier_payouts.map((p, i) => (
                            <div key={p.id || i} className="flex items-center gap-1.5 text-[12px]">
                              <span className="text-[#8a8a8a]">J{p.level_order || i + 1}:</span>
                              <span className="text-[#646464]">{p.jumlah_view.toLocaleString('id-ID')}v</span>
                              <span className="text-[#8a8a8a]">→</span>
                              <span className="font-semibold text-[#3fb5d2]">{fmtCurrency(p.jumlah_payout)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[12px] text-[#8a8a8a]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button onClick={() => handleOpenModal(tier)} className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] mr-3 transition-colors">Edit</button>
                      <button onClick={() => handleDelete(tier.id)} className="text-[12px] font-medium text-[#c62828] hover:text-red-700 transition-colors">Hapus</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={card}>
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <h2 className="text-[16px] font-semibold text-[#3b3b3b] mb-5">
                  {editingTier ? 'Edit Tier' : 'Tambah Tier Baru'}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className={LABEL}>Nama Tier *</label>
                    <input type="text" required value={formData.nama}
                      onChange={(e) => setFormData(p => ({ ...p, nama: e.target.value }))}
                      className={INPUT} placeholder="Contoh: Tier Bronze" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={LABEL}>Minimum View *</label>
                      <input type="number" required min="0" value={formData.minimum_view}
                        onChange={(e) => setFormData(p => ({ ...p, minimum_view: e.target.value }))}
                        className={INPUT} placeholder="0" />
                    </div>
                    <div>
                      <label className={LABEL}>Maximum View *</label>
                      <input type="number" required min="1" value={formData.maximum_view}
                        onChange={(e) => setFormData(p => ({ ...p, maximum_view: e.target.value }))}
                        className={INPUT} placeholder="10000" />
                    </div>
                  </div>
                  <div>
                    <label className={LABEL}>Bounty Approved Value</label>
                    <input type="number" min="0" step="0.01" value={formData.bounty_approved_value}
                      onChange={(e) => setFormData(p => ({ ...p, bounty_approved_value: e.target.value }))}
                      className={INPUT} placeholder="0" />
                    <p className="text-[11px] text-[#8a8a8a] mt-1">Nilai bounty ketika proposal disetujui</p>
                  </div>

                  {/* Jenjang Payout */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={LABEL + ' mb-0'}>Jenjang Payout *</label>
                      <button type="button" onClick={addPayout}
                        className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] transition-colors">
                        + Tambah Jenjang
                      </button>
                    </div>
                    <div className="border border-[#e5e5e5] rounded-xl p-4 space-y-3 max-h-72 overflow-y-auto">
                      {formData.payouts.map((payout, index) => (
                        <div key={index} className="flex items-end gap-3 p-3 bg-[#f5f5f5] rounded-lg">
                          <div className="flex-1">
                            <label className={LABEL}>J{index + 1} · Jumlah View *</label>
                            <input type="number" required min="1" value={payout.jumlah_view}
                              onChange={(e) => updatePayout(index, 'jumlah_view', e.target.value)}
                              className={INPUT} placeholder="1000" />
                          </div>
                          <div className="flex-1">
                            <label className={LABEL}>Jumlah Payout *</label>
                            <input type="number" required min="0" step="0.01" value={payout.jumlah_payout}
                              onChange={(e) => updatePayout(index, 'jumlah_payout', e.target.value)}
                              className={INPUT} placeholder="50000" />
                          </div>
                          {formData.payouts.length > 1 && (
                            <button type="button" onClick={() => removePayout(index)}
                              className="mb-0.5 text-[12px] text-[#c62828] hover:text-red-700 transition-colors flex-shrink-0">
                              Hapus
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-[#f5f5f5] rounded-b-2xl flex justify-end gap-3">
                <button type="button" onClick={handleCloseModal}
                  className="px-4 py-2 text-[13px] font-medium text-[#646464] border border-[#e5e5e5] rounded-lg bg-white hover:bg-gray-50 transition-colors">
                  Batal
                </button>
                <button type="submit"
                  className="px-4 py-2 text-[13px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white rounded-lg transition-colors">
                  {editingTier ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tiers;
